import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  getOllieApiError,
  loadOllieFindings,
  markFindingsStale,
  setFeedbackFollowUp,
} from 'src/features/OllieSlice'
import useOllieVenueId from 'src/hooks/useOllieVenueId'
import {
  confirmOllieAction,
  markOllieDraftUsed,
  prepareOllieAction,
  reportOllieFinding,
} from 'src/utils/ollieFindingsApi'

/**
 * One finding's actions: prepare (review, saves nothing) → confirm (saves,
 * only after the user pressed confirm), supplier drafts (copy / open, never
 * sent by OMS) and "This isn't right". State is local to the card / sheet.
 *
 * @param {{ checkId: string }} finding
 */
const useOllieFindingAction = finding => {
  const dispatch = useDispatch()
  const { venueId } = useOllieVenueId()
  const checkId = finding?.checkId
  const [status, setStatus] = useState('idle')
  const [prepared, setPrepared] = useState(null)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const run = useCallback(async (nextStatus, request) => {
    setStatus(nextStatus)
    setError(null)
    try {
      const data = await request()
      setStatus('idle')
      return { success: true, ...data }
    } catch (requestError) {
      const parsed = getOllieApiError(requestError)
      setStatus('error')
      setError(parsed)
      return { success: false, ...parsed }
    }
  }, [])

  const prepare = useCallback(
    async actionId => {
      const outcome = await run('preparing', () =>
        prepareOllieAction({ venueId, checkId, actionId }),
      )
      if (outcome.success) setPrepared({ actionId, ...outcome.prepared })
      return outcome
    },
    [run, venueId, checkId],
  )

  const confirm = useCallback(
    async (actionId, { items, fields } = {}) => {
      const outcome = await run('confirming', () =>
        confirmOllieAction({ venueId, checkId, actionId, items, fields }),
      )
      if (outcome.success) {
        setResult(outcome.result)
        dispatch(markFindingsStale())
        dispatch(loadOllieFindings({ venueId, force: true }))
      }
      return outcome
    },
    [run, dispatch, venueId, checkId],
  )

  const draftUsed = useCallback(
    (actionId, via, supplierId) =>
      run('recording', () =>
        markOllieDraftUsed({ venueId, checkId, actionId, via, supplierId }),
      ),
    [run, venueId, checkId],
  )

  const report = useCallback(
    async ({ reason, comment, surface }) => {
      const outcome = await run('reporting', () =>
        reportOllieFinding({ venueId, checkId, reason, comment, surface }),
      )
      if (outcome.success) {
        dispatch(setFeedbackFollowUp(outcome.followUp))
        // "Already sorted" and Express found it gone: drop it everywhere.
        if (outcome.followUp?.recheck?.outcome === 'fixed') {
          dispatch(markFindingsStale())
          dispatch(loadOllieFindings({ venueId, force: true }))
        }
      }
      return outcome
    },
    [run, dispatch, venueId, checkId],
  )

  const reset = useCallback(() => {
    setStatus('idle')
    setPrepared(null)
    setResult(null)
    setError(null)
  }, [])

  return {
    status,
    prepared,
    result,
    error,
    prepare,
    confirm,
    draftUsed,
    report,
    reset,
  }
}

export default useOllieFindingAction
